import { useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';

/**
 * Clean preview for photo prints (posters, canvas, "fotoutskrift").
 * Shows the customer's uploaded photo at the correct print aspect ratio
 * based on the chosen orientation. No product mockup — the customer sees
 * exactly what will be printed.
 */
const PhotoPrintPreview = ({ texture, backgroundColor = '#ffffff', orientation = 'portrait', designConfig = null }) => {
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1);

  const aspectClass =
    orientation === 'landscape' ? 'aspect-[3/2]' : 'aspect-[2/3]';

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.05 : 0.05;
      setZoom((z) => Math.max(0.7, Math.min(1.6, z + delta)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const config = designConfig || { position_x: 50, position_y: 50, scale: 1, rotation: 0 };
  const imageStyle = {
    position: 'absolute',
    width: `${100 * (config.scale || 1)}%`,
    height: `${100 * (config.scale || 1)}%`,
    left: `${config.position_x || 50}%`,
    top: `${config.position_y || 50}%`,
    transform: `translate(-50%, -50%) rotate(${config.rotation || 0}deg)`,
    objectFit: 'cover',
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full min-h-[400px] flex items-center justify-center select-none overflow-hidden rounded-xl"
      style={{ background: 'linear-gradient(180deg, #fafafa 0%, #f0f0f0 100%)' }}
      data-testid="photo-print-preview"
    >
      <div className="absolute top-4 left-4 z-10">
        <button
          onClick={() => setZoom(1)}
          className="p-2 rounded-full bg-white/90 text-slate-600 hover:bg-white shadow-md transition-all"
          title="Återställ zoom"
          data-testid="reset-photo-preview-btn"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>

      <div
        className={`relative ${aspectClass} w-[70%] max-h-[85%] bg-white overflow-hidden`}
        style={{
          transform: `scale(${zoom})`,
          boxShadow: '0 20px 40px rgba(0,0,0,0.18), 0 0 0 8px #ffffff, 0 0 0 10px rgba(0,0,0,0.05)',
          transition: 'transform 0.1s ease-out',
          backgroundColor,
        }}
      >
        {texture ? (
          <img src={texture} alt="Din design" style={imageStyle} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300 text-sm">
            Ladda upp en bild för att se förhandsvisningen
          </div>
        )}
      </div>

      <div className="absolute bottom-4 left-0 right-0 text-center">
        <span className="inline-block px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full text-sm text-slate-600 shadow-md">
          {texture
            ? `${orientation === 'landscape' ? 'Liggande' : 'Stående'} — så här kommer bilden att tryckas`
            : 'Ladda upp en bild för att se förhandsvisningen'}
        </span>
      </div>

      {zoom !== 1 && (
        <div className="absolute top-4 right-4 px-3 py-1 bg-black/70 text-white text-xs rounded-full">
          {Math.round(zoom * 100)}%
        </div>
      )}
    </div>
  );
};

export default PhotoPrintPreview;
